import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import CompanyList from '../../components/admin/companies/CompanyList';
import { FiHome } from 'react-icons/fi';
import { apiClient } from '../../services/api';
import { useDialog } from '../../hooks/useDialog';
import { useServiceTypes } from '../../hooks/useServiceTypes';
import toast from 'react-hot-toast';

// VERSION CHECK
console.log('📄 [CompanyManagement] Loading CompanyManagement.tsx - Commit: bae9dd1');

interface Company {
  id: string;
  name: string;
  nameEn?: string;
  email: string;
  phone: string;
  province: string;
  district: string;
  services: string[];
  status: 'active' | 'pending' | 'suspended' | 'rejected';
  isVerified: boolean;
  isFeatured: boolean;
  createdAt: string;
  lastUpdated: string;
  viewCount: number;
  contactCount: number;
  logo?: string;
}

interface CompanyStats {
  total_companies: number;
  active_companies: number;
  verified_companies: number;
  featured_companies: number;
  verification_rate: number;
  companies_by_province: Record<string, number>;
  companies_by_size: Record<string, number>;
  recent_additions: number;
}

const CompanyManagement: React.FC = () => {
  const navigate = useNavigate();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();
  const { serviceTypes, loading: serviceTypesLoading } = useServiceTypes();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('');
  const [verifiedFilter, setVerifiedFilter] = useState<string>('');

  const breadcrumbs = [
    { label: 'จัดการบริษัท', icon: FiHome }
  ];

  // Fetch companies and stats on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Debug: Check apiClient baseURL
        console.log('🔍 [CompanyManagement] apiClient.defaults.baseURL:', apiClient.defaults.baseURL);

        // Fetch companies
        const companiesResponse = await apiClient.get('/api/admin/companies', {
          params: { page: 1, per_page: 100 }
        });

        // Fetch stats
        const statsResponse = await apiClient.get('/api/admin/companies/stats');

        // Map API response to UI format
        const mappedCompanies: Company[] = companiesResponse.data.companies.map((apiCompany: any) => ({
          id: apiCompany.id,
          name: apiCompany.nameTh,
          nameEn: apiCompany.nameEn,
          email: apiCompany.email || '',
          phone: apiCompany.phone1 || '',
          province: apiCompany.province,
          district: apiCompany.district || '',
          services: Array.isArray(apiCompany.serviceTypes) ? apiCompany.serviceTypes : [],
          status: apiCompany.isActive ? 'active' : 'suspended',
          isVerified: apiCompany.isVerified,
          isFeatured: apiCompany.isFeatured,
          createdAt: apiCompany.createdAt,
          lastUpdated: apiCompany.updatedAt,
          viewCount: apiCompany.viewCount || 0,
          contactCount: apiCompany.contactCount || 0,
          logo: apiCompany.logoUrl
        }));

        setCompanies(mappedCompanies);
        setStats(statsResponse.data);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = (company: Company) => {
    navigate(`/admin/companies/edit/${company.id}`);
  };

  const handleView = (company: Company) => {
    navigate(`/admin/companies/view/${company.id}`);
  };

  const handleDelete = async (companyId: string) => {
    await showConfirm(
      {
        title: 'ยืนยันการลบ',
        message: 'คุณต้องการลบบริษัทนี้ใช่หรือไม่?',
        confirmText: 'ลบ',
        cancelText: 'ยกเลิก',
        variant: 'danger'
      },
      async () => {
        setLoading(true);
        try {
          // API call to delete company (soft delete)
          await apiClient.delete(`/api/admin/companies/${companyId}`);

          // Remove from UI list
          setCompanies(companies.filter(c => c.id !== companyId));

          // Update stats
          if (stats) {
            setStats({
              ...stats,
              total_companies: stats.total_companies - 1,
              active_companies: stats.active_companies - 1
            });
          }

          toast.success('ลบบริษัทเรียบร้อยแล้ว');
        } catch (error: any) {
          console.error('Error deleting company:', error);
          toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการลบบริษัท');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleStatusChange = async (companyId: string, status: Company['status']) => {
    setLoading(true);
    try {
      // API call to update company status
      console.log('Updating company status:', companyId, status);
      // After successful update, refresh the list
      // await refreshCompanyList();
    } catch (error) {
      console.error('Error updating company status:', error);
      toast.error('เกิดข้อผิดพลาดในการอัพเดทสถานะ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout
      title="จัดการบริษัท"
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FiHome className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      บริษัททั้งหมด
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {loading ? (
                        <div className="animate-pulse h-6 bg-gray-200 rounded w-16"></div>
                      ) : (
                        stats?.total_companies.toLocaleString() || '0'
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="h-3 w-3 bg-green-600 rounded-full"></div>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      ใช้งานอยู่
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {loading ? (
                        <div className="animate-pulse h-6 bg-gray-200 rounded w-16"></div>
                      ) : (
                        stats?.active_companies.toLocaleString() || '0'
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <div className="h-3 w-3 bg-blue-600 rounded-full"></div>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      ยืนยันแล้ว
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {loading ? (
                        <div className="animate-pulse h-6 bg-gray-200 rounded w-16"></div>
                      ) : (
                        stats?.verified_companies.toLocaleString() || '0'
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 bg-yellow-100 rounded-full flex items-center justify-center">
                    <div className="h-3 w-3 bg-yellow-600 rounded-full"></div>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      แนะนำ
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {loading ? (
                        <div className="animate-pulse h-6 bg-gray-200 rounded w-16"></div>
                      ) : (
                        stats?.featured_companies.toLocaleString() || '0'
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company List */}
        <CompanyList
          companies={companies}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          serviceTypes={serviceTypes}
          serviceTypesLoading={serviceTypesLoading}
          serviceTypeFilter={serviceTypeFilter}
          onServiceTypeFilterChange={setServiceTypeFilter}
          verifiedFilter={verifiedFilter}
          onVerifiedFilterChange={setVerifiedFilter}
        />
      </div>

      {ConfirmDialog}
      {AlertDialog}
    </AdminLayout>
  );
};

export default CompanyManagement;